import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./components/Login";
import Sidebar, { SidebarToggleButton } from "./components/Sidebar";
import Dashboard from "./components/Dashboard";
import UsuariosList from "./components/UsuariosList";
import EmpleadoDetail from "./components/EmpleadoDetail";
import Employees from "./components/Employees";
import LoginTrabajador from "./components/LoginTrabajador";
import EmpleadosList from "./components/EmpleadosList";
import AdministradoresList from "./components/AdministradoresList";
import TransportistasList from "./components/TransportistasList";
import TransportistaCreate from "./components/TransportistaCreate";
import TransportistaDetail from "./components/TransportistaDetail";
import VehiculosList from "./components/VehiculosList";
import VehiculoDetail from "./components/VehiculoDetail";
import TiposList from "./components/TiposList";
import Empresa from "./components/Empresa";
import DocumentacionList from "./components/DocumentacionList";
import "react-toastify/dist/ReactToastify.css";

const PrivateRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return <div className="text-center mt-5">Cargando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Componente para redirección inteligente según el rol del usuario
const SmartRedirect = () => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="text-center mt-5">Cargando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Si es administrador, redirigir al listado de empleados
  if (user?.ROL === 'admin') {
    return <Navigate to="/empleados" />;
  }

  // Si es trabajador, redirigir a su perfil
  if (user?.ROL !== 'admin') {
    const userId = user?.ID || user?.id || user?.empleado_id || user?.empleadoId;
    if (userId) {
      return <Navigate to={`/empleado-detail?empleado_id=${userId}`} />;
    }
  }

  // Fallback al dashboard si no se puede determinar el rol
  return <Navigate to="/dashboard" />;
};

const Layout = ({ children }) => {
  const [showSidebar, setShowSidebar] = React.useState(false);

  // Cierra el sidebar al cambiar de tamaño a desktop
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setShowSidebar(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      {/* Botón hamburguesa solo en móvil */}
      <SidebarToggleButton onClick={() => setShowSidebar(true)} />
      {/* Sidebar como drawer en móvil, fijo en desktop */}
      <div>
        <div className="d-none d-md-block">
          <Sidebar show={true} />
        </div>
        <div className="d-md-none">
          <Sidebar show={showSidebar} onClose={() => setShowSidebar(false)} />
        </div>
      </div>
      <div className="main-content">{children}</div>
    </>
  );
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/login-trabajador" element={<LoginTrabajador />} />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/usuarios"
        element={
          <PrivateRoute requireAdmin>
            <Layout>
              <UsuariosList />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/empleado-detail"
        element={
          <PrivateRoute>
            <Layout>
              <EmpleadoDetail />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/gastos"
        element={
          <PrivateRoute>
            <Layout>
              <Employees />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/empleados"
        element={
          <PrivateRoute requireAdmin>
            <Layout>
              <EmpleadosList />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/tipos"
        element={
          <PrivateRoute requireAdmin>
            <Layout>
              <TiposList />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/documentacion"
        element={
          <PrivateRoute requireAdmin>
            <Layout>
              <DocumentacionList />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/empresa"
        element={
          <PrivateRoute requireAdmin>
            <Layout>
              <Empresa />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/administradores"
        element={
          <PrivateRoute requireAdmin>
            <Layout>
              <AdministradoresList />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/transportistas"
        element={
          <PrivateRoute requireAdmin>
            <Layout>
              <TransportistasList />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/transportistas/nuevo"
        element={
          <PrivateRoute requireAdmin>
            <Layout>
              <TransportistaCreate />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/vehiculos"
        element={
          <PrivateRoute requireAdmin>
            <Layout>
              <VehiculosList />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/vehiculo-detail"
        element={
          <PrivateRoute requireAdmin>
            <Layout>
              <VehiculoDetail />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/transportista-detail"
        element={
          <PrivateRoute requireAdmin>
            <Layout>
              <TransportistaDetail />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route path="/" element={<SmartRedirect />} />
    </Routes>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppRoutes />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </AuthProvider>
  );
};

export default App;
